import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ConnectionLineType,
  Panel,
  ViewportPortal,
  type Connection,
  type Node,
  type Edge,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { nodeTypes } from './nodeTypes';
import { edgeDisplayName, edgeVisual } from './edgeTypes';
import { AddNodeButton } from './AddNodeButton';
import { SegmentFilter } from './SegmentFilter';
import { useCanvasStore, useUIStore } from '@/stores';
import { UNSEGMENTED } from '@/stores/ui-store';
import { Button } from '@/components/ui/button';
import { GitBranch, Target, RotateCcw, MessagesSquare, Sparkles, Layers, Eye } from 'lucide-react';
import type { NodeMetadata } from '@/services/canvas-mappers';
import { deriveSynthesisEdges, deriveClusteredRawIds } from '@/services/synthesis-edges';
import { useSpineLayout } from '@/hooks/use-spine-layout';
import { useCanvasMutations } from '@/hooks/use-canvas-mutations';
import { inferEdgeType, type ConnectableNode } from '@/lib/edge-rules';
import { toast } from '@/hooks/use-toast';
import { NODE_HUE } from '@/lib/node-visuals';
import { deriveRegionBoxes, nodeTypeToRegion, REGION_LABELS } from '@/lib/graph-regions';

function findDownstream(nodeId: string, edges: Edge[]): Set<string> {
  const visited = new Set<string>();
  const queue = [nodeId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (visited.has(current)) continue;
    visited.add(current);
    edges.filter((e) => e.source === current).forEach((e) => queue.push(e.target));
  }
  visited.delete(nodeId);
  return visited;
}

function findUpstream(nodeId: string, edges: Edge[]): Set<string> {
  const visited = new Set<string>();
  const queue = [nodeId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (visited.has(current)) continue;
    visited.add(current);
    edges.filter((e) => e.target === current).forEach((e) => queue.push(e.source));
  }
  visited.delete(nodeId);
  return visited;
}

function connectableById(id: string | null): ConnectableNode | null {
  const n = useCanvasStore.getState().nodes.find((node) => node.id === id);
  return n ? { type: n.type, metadata: n.data?.metadata as NodeMetadata | undefined } : null;
}

export function FlowCanvas({ projectId }: { projectId: string }) {
  const { nodes, edges, onNodesChange, onEdgesChange } = useCanvasStore();
  const { connectEdge, persistRemoved } = useCanvasMutations(projectId);
  const nodeSourceFilter = useUIStore((s) => s.nodeSourceFilter);
  const setNodeSourceFilter = useUIStore((s) => s.setNodeSourceFilter);
  const showEvidence = useUIStore((s) => s.showEvidence);
  const setShowEvidence = useUIStore((s) => s.setShowEvidence);
  const segmentField = useUIStore((s) => s.segmentField);
  const segmentFilter = useUIStore((s) => s.segmentFilter);
  const canEdit = useUIStore((s) => s.canEdit);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [analysisMode, setAnalysisMode] = useState<'none' | 'downstream' | 'upstream'>('none');

  // Raw dialog items that have been absorbed into a synthesised node. In the
  // synthesised view these are hidden — their synth node represents them —
  // while un-clustered raw stays visible.
  const clusteredRawIds = useMemo(
    () =>
      deriveClusteredRawIds(
        nodes.map((n) => {
          const meta = n.data?.metadata as NodeMetadata | undefined;
          return { id: n.id, source: meta?.source, memberItemIds: meta?.memberItemIds };
        }),
      ),
    [nodes],
  );

  // Combined node filter, three independent axes:
  //  - source: raw (dialog) vs synthesised vs all — only constrains
  //    dialog-derived nodes; idea/capability/manual nodes always pass it.
  //  - evidence: hide the market_signal + assumption overlay.
  //  - segment: multi-select on the chosen respondent dimension.
  // Each permissive setting short-circuits so the unfiltered case doesn't
  // allocate. Edges touching a hidden node are dropped below.
  const filteredNodes = useMemo(() => {
    const segmentSet = segmentFilter.length > 0 ? new Set(segmentFilter) : null;
    if (nodeSourceFilter === 'all' && showEvidence && !segmentSet) return nodes;
    return nodes.filter((node) => {
      const meta = node.data?.metadata as NodeMetadata | undefined;
      if (!showEvidence && (node.type === 'market_signal' || node.type === 'assumption')) {
        return false;
      }
      if (nodeSourceFilter === 'synthesised') {
        // Primary synthesised view: synth nodes + still-un-clustered raw
        // findings (kept so "what's left to synthesise?" stays visible). A raw
        // item already absorbed into a synth node is hidden — its synth node
        // represents it (detail via the node's provenance). "Replace isn't total."
        if (meta?.source === 'dialog' && clusteredRawIds.has(node.id)) return false;
      } else if (nodeSourceFilter === 'raw') {
        if (meta?.source === 'synthesis') return false;
      }
      if (segmentSet) {
        const value = meta?.segment?.[segmentField];
        const key = value && value.length > 0 ? value : UNSEGMENTED;
        if (!segmentSet.has(key)) return false;
      }
      return true;
    });
  }, [nodes, nodeSourceFilter, showEvidence, segmentField, segmentFilter, clusteredRawIds]);

  // Derived synthesised-spine edges: lift the raw impedes/improves edges through
  // cluster membership so synthesised nodes connect to each other (and to
  // surviving raw nodes). Pure + computed from the current graph — see
  // synthesis-edges.ts. Interim styling (distinct stroke so they read apart from
  // the still-visible raw edges); the edge styling/visibility pass owns that.
  const derivedEdges = useMemo<Edge[]>(() => {
    const descriptors = deriveSynthesisEdges(
      nodes.map((n) => {
        const meta = n.data?.metadata as NodeMetadata | undefined;
        return { id: n.id, source: meta?.source, memberItemIds: meta?.memberItemIds };
      }),
      edges.map((e) => ({
        source: e.source,
        target: e.target,
        // The relationship type is the enum in data.edgeType, not the
        // presentation `label` (a display name that may diverge from it).
        edgeType: (e.data as { edgeType?: string } | undefined)?.edgeType ?? '',
      })),
    );
    return descriptors.map((d) => ({
      id: d.id,
      source: d.source,
      target: d.target,
      label: edgeDisplayName(d.edgeType),
      type: 'default',
      ...edgeVisual(d.edgeType),
      // Derived edges aren't persisted in the canvas store, so they must be
      // non-interactive — otherwise a select/delete emits an onEdgesChange the
      // store can't honour and the edge just reappears on the next render.
      selectable: false,
      deletable: false,
      data: { derived: true },
    }));
  }, [nodes, edges]);

  const baseEdges = useMemo(() => [...edges, ...derivedEdges], [edges, derivedEdges]);

  const filteredEdges = useMemo(() => {
    const segmentActive = segmentFilter.length > 0;
    if (nodeSourceFilter === 'all' && showEvidence && !segmentActive) return baseEdges;
    const visibleIds = new Set(filteredNodes.map((n) => n.id));
    return baseEdges.filter((e) => visibleIds.has(e.source) && visibleIds.has(e.target));
  }, [baseEdges, filteredNodes, nodeSourceFilter, showEvidence, segmentFilter]);

  // Spine auto-layout (elk, left→right partitioned columns), computed over the
  // currently-visible set so each filter gets a compact, overlap-free
  // arrangement (the engine de-overlaps each column). Positions shift when a
  // filter toggles — that's the trade for not stacking hidden nodes' reserved
  // slots into the visible view. Shared with the journey mini graph (which has
  // no filters, so it lays out the whole graph) via useSpineLayout.
  const layoutPos = useSpineLayout(filteredNodes, filteredEdges);

  const rfRef = useRef<ReactFlowInstance | null>(null);

  // Clear analysis selection if the user filtered out the focal node —
  // otherwise styledNodes/styledEdges treat every visible node as "unrelated"
  // and dim the whole canvas. Analysis mode itself stays on (the user can
  // pick a new focal node, or click Reset).
  useEffect(() => {
    if (selectedNodeId && !filteredNodes.some((n) => n.id === selectedNodeId)) {
      setSelectedNodeId(null);
    }
  }, [filteredNodes, selectedNodeId]);

  const highlightedIds = useMemo(() => {
    if (!selectedNodeId || analysisMode === 'none') return new Set<string>();
    if (analysisMode === 'downstream') return findDownstream(selectedNodeId, filteredEdges);
    return findUpstream(selectedNodeId, filteredEdges);
  }, [selectedNodeId, analysisMode, filteredEdges]);

  // Apply computed positions. Both spine and overlay nodes are in layoutPos
  // (the engine positions both), so they become non-draggable — layout is the
  // single source of truth. Anything not in the map keeps its stored position.
  const positionedNodes = useMemo(
    () =>
      filteredNodes.map((node) => {
        const p = layoutPos.get(node.id);
        return p ? { ...node, position: p, draggable: false } : node;
      }),
    [filteredNodes, layoutPos],
  );

  // Band backdrop: the same geography the peek schematic teaches, drawn under
  // the live nodes so the mental map transfers to full screen. Recomputed with
  // layout — cheap (one pass over nodes).
  const regionBoxes = useMemo(
    () =>
      deriveRegionBoxes(
        positionedNodes.map((n) => ({
          type: n.type ?? '',
          position: n.position,
          width: n.measured?.width,
          height: n.measured?.height,
        })),
      ),
    [positionedNodes],
  );

  const graphFocusRegion = useUIStore((s) => s.graphFocusRegion);
  const setGraphFocusRegion = useUIStore((s) => s.setGraphFocusRegion);
  // One framing decision per fresh layout: a pending region handoff frames
  // its territory, otherwise the whole graph. This effect is deliberately the
  // ONLY viewport-fitting actor — the declarative fitView prop is omitted
  // from <ReactFlow> because its deferred initial fit (it waits for every
  // node to finish measuring) fires after this effect on a fresh mount and
  // stomped the region frame with a whole-graph fit. Refs keep the effect
  // firing on layout arrival only, not when boxes recompute on measurement.
  const framingRef = useRef({ graphFocusRegion, positionedNodes });
  framingRef.current = { graphFocusRegion, positionedNodes };
  useEffect(() => {
    // Wait for the fresh elk layout: before it resolves, positionedNodes
    // falls back to stored positions and would frame stale geometry.
    if (layoutPos.size === 0) return;
    const { graphFocusRegion: region, positionedNodes: pNodes } = framingRef.current;
    const regionNodes = region
      ? pNodes.filter((n) => nodeTypeToRegion(n.type ?? '') === region).map((n) => ({ id: n.id }))
      : [];
    // Zoom floors mirror the retired mini render's proven framing: the whole
    // graph may need a tiny zoom (the evidence bands run very wide), while a
    // region frame stops at a legible window and crops the excess rather
    // than shrinking a wide band into unreadability.
    if (regionNodes.length > 0) {
      rfRef.current?.fitView({
        nodes: regionNodes,
        duration: 300,
        padding: 0.2,
        minZoom: 0.45,
        maxZoom: 1.2,
      });
    } else {
      rfRef.current?.fitView({ duration: 300, padding: 0.15, minZoom: 0.05, maxZoom: 1.2 });
    }
    if (region) setGraphFocusRegion(null);
  }, [layoutPos, setGraphFocusRegion]);

  // A one-shot handoff that outlives the visit is stale: if the user leaves
  // the graph before layout resolves (or the graph never produces boxes),
  // drop it so a later visit doesn't zoom somewhere unprompted. FlowCanvas
  // always mounts in the same handler tick that sets the region, so this
  // cleanup can never race the handoff's arrival (no StrictMode in this app).
  useEffect(() => () => setGraphFocusRegion(null), [setGraphFocusRegion]);

  const styledNodes = useMemo(() => {
    if (!selectedNodeId || analysisMode === 'none') return positionedNodes;
    return positionedNodes.map((node) => {
      const isSelected = node.id === selectedNodeId;
      const isHighlighted = highlightedIds.has(node.id);
      return {
        ...node,
        style: {
          ...node.style,
          opacity: isSelected || isHighlighted ? 1 : 0.25,
          transition: 'opacity 130ms ease',
        },
      };
    });
  }, [positionedNodes, selectedNodeId, highlightedIds, analysisMode]);

  const styledEdges = useMemo(() => {
    if (!selectedNodeId || analysisMode === 'none') return filteredEdges;
    const relatedNodeIds = new Set([selectedNodeId, ...highlightedIds]);
    return filteredEdges.map((edge) => ({
      ...edge,
      style: {
        ...edge.style,
        opacity: relatedNodeIds.has(edge.source) && relatedNodeIds.has(edge.target) ? 1 : 0.1,
        strokeWidth: relatedNodeIds.has(edge.source) && relatedNodeIds.has(edge.target) ? 2 : 1,
      },
    }));
  }, [filteredEdges, selectedNodeId, highlightedIds, analysisMode]);

  // The relationship type is derived from the node pair (edge-rules.ts), never
  // chosen by the user; invalid pairs are blocked at drag time below and (belt
  // and braces) rejected here with a toast.
  const isValidConnection = useCallback((conn: Connection | Edge) => {
    const source = connectableById(conn.source);
    const target = connectableById(conn.target);
    return !!source && !!target && inferEdgeType(source, target) !== null;
  }, []);

  const onConnect = useCallback(
    (params: Connection) => {
      const source = connectableById(params.source);
      const target = connectableById(params.target);
      const edgeType = source && target ? inferEdgeType(source, target) : null;
      if (!edgeType) {
        toast({
          title: 'Invalid connection',
          description: "That pair isn't a relationship in the value graph.",
          variant: 'destructive',
        });
        return;
      }
      const newEdge = { ...params, type: 'default', label: edgeDisplayName(edgeType), data: { edgeType }, ...edgeVisual(edgeType) };
      connectEdge(newEdge);
    },
    [connectEdge]
  );

  // Delete key: React Flow has already removed the elements from the store via
  // onNodesChange/onEdgesChange (including a deleted node's connected edges) —
  // this just persists what was removed.
  const onDelete = useCallback(
    (deleted: { nodes: Node[]; edges: Edge[] }) => persistRemoved(deleted.nodes, deleted.edges),
    [persistRemoved]
  );

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    // Ignore the second click of a double-click (detail > 1) — otherwise a
    // double-click toggles an analysis selection straight back off.
    if (event.detail > 1) return;
    // In analysis mode a click picks the focal node; otherwise it opens the
    // node's detail/provenance modal. Single-click (not double) because nodes
    // are no longer draggable and double-click is taken by canvas zoom. There's
    // deliberately no onNodeDoubleClick — the first click of any double-click
    // already opens the modal, and a separate handler would also wrongly fire
    // in analysis mode.
    if (analysisMode !== 'none') {
      setSelectedNodeId((prev) => (prev === node.id ? null : node.id));
      return;
    }
    useUIStore.getState().setActiveModal(`edit-node:${node.id}`);
  }, [analysisMode]);

  const resetAnalysis = () => {
    setSelectedNodeId(null);
    setAnalysisMode('none');
  };

  const selectedNode = filteredNodes.find((n) => n.id === selectedNodeId);
  const { rawCount, synthCount, evidenceCount } = useMemo(() => {
    let raw = 0;
    let synth = 0;
    let evidence = 0;
    for (const n of nodes) {
      const src = (n.data?.metadata as NodeMetadata | undefined)?.source;
      if (src === 'dialog') raw += 1;
      else if (src === 'synthesis') synth += 1;
      if (n.type === 'market_signal' || n.type === 'assumption') evidence += 1;
    }
    return { rawCount: raw, synthCount: synth, evidenceCount: evidence };
  }, [nodes]);

  return (
    <ReactFlow
      nodes={styledNodes}
      edges={styledEdges}
      onInit={(instance) => {
        rfRef.current = instance;
      }}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      // Read-only for viewers: no new edges, and the Delete key can't remove
      // nodes/edges. (Nodes are already non-draggable — layout owns position.)
      onConnect={canEdit ? onConnect : undefined}
      onDelete={canEdit ? onDelete : undefined}
      isValidConnection={isValidConnection}
      nodesConnectable={canEdit}
      deleteKeyCode={canEdit ? undefined : null}
      onNodeClick={onNodeClick}
      nodeTypes={nodeTypes}
      connectionLineType={ConnectionLineType.Bezier}
      // Click opens the node modal; don't let double-click zoom the canvas (it
      // stole the interaction and made the modal unreachable).
      zoomOnDoubleClick={false}
      // React Flow's default floor (0.5) can't fit a graph whose evidence
      // bands run wide — the whole-graph frame needs room to zoom out, and
      // the pan/zoom extent must allow what fitView computes.
      minZoom={0.05}
      className="bg-background"
    >
      <Background variant={BackgroundVariant.Dots} gap={22} size={1} color="hsl(var(--border))" />
      <ViewportPortal>
        {regionBoxes.map(({ region, rect }) => (
          <div
            key={region}
            style={{
              position: 'absolute',
              transform: `translate(${rect.x}px, ${rect.y}px)`,
              width: rect.width,
              height: rect.height,
              zIndex: -1,
              pointerEvents: 'none',
            }}
            className="rounded-xl bg-muted/40 border border-border/40"
          >
            <span className="absolute top-1.5 left-2.5 text-[11px] uppercase tracking-wide text-muted-foreground/70 select-none">
              {REGION_LABELS[region]}
            </span>
          </div>
        ))}
      </ViewportPortal>
      <Controls className="bg-background border shadow-xs" />
      <MiniMap
        className="bg-background border shadow-xs"
        nodeColor={(node) => NODE_HUE[node.type || ''] || '#94A3B8'}
      />
      <Panel position="top-right" className="flex gap-2">
        <AddNodeButton projectId={projectId} />
      </Panel>
      <Panel position="bottom-center">
        <div className="bg-background/90 backdrop-blur-sm border rounded-md p-2 shadow-xs space-y-2">
          <div className="text-xs font-semibold text-muted-foreground">Show nodes</div>
          <div className="flex gap-1 items-center">
            <Button
              size="sm"
              variant={nodeSourceFilter === 'all' ? 'default' : 'outline'}
              className="h-7 text-xs"
              onClick={() => setNodeSourceFilter('all')}
            >
              <Eye className="h-3 w-3 mr-1" />
              All
            </Button>
            <Button
              size="sm"
              variant={nodeSourceFilter === 'raw' ? 'default' : 'outline'}
              className="h-7 text-xs"
              onClick={() => setNodeSourceFilter('raw')}
              disabled={rawCount === 0}
              title={rawCount === 0 ? 'No raw dialog nodes yet' : 'Raw dialog extractions'}
            >
              <MessagesSquare className="h-3 w-3 mr-1" />
              Raw{rawCount > 0 && ` (${rawCount})`}
            </Button>
            <Button
              size="sm"
              variant={nodeSourceFilter === 'synthesised' ? 'default' : 'outline'}
              className="h-7 text-xs"
              onClick={() => setNodeSourceFilter('synthesised')}
              disabled={synthCount === 0}
              title={synthCount === 0 ? 'No synthesised nodes yet' : 'Synthesised clusters'}
            >
              <Sparkles className="h-3 w-3 mr-1" />
              Synthesised{synthCount > 0 && ` (${synthCount})`}
            </Button>
            <div className="w-px h-5 bg-border mx-1" />
            <Button
              size="sm"
              variant={showEvidence ? 'default' : 'outline'}
              className="h-7 text-xs"
              onClick={() => setShowEvidence(!showEvidence)}
              disabled={evidenceCount === 0}
              title={
                evidenceCount === 0
                  ? 'No market findings or assumptions yet'
                  : 'Toggle the evidence overlay (market findings + assumptions)'
              }
            >
              <Layers className="h-3 w-3 mr-1" />
              Evidence{evidenceCount > 0 && ` (${evidenceCount})`}
            </Button>
            <SegmentFilter />
          </div>
        </div>
      </Panel>
      <Panel position="top-left">
        <div className="bg-background/90 backdrop-blur-sm border rounded-md p-2 shadow-xs space-y-2">
          <div className="text-xs font-semibold text-muted-foreground">Impact Analysis</div>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant={analysisMode === 'downstream' ? 'default' : 'outline'}
              className="h-7 text-xs"
              onClick={() => {
                setAnalysisMode(analysisMode === 'downstream' ? 'none' : 'downstream');
                if (analysisMode === 'downstream') setSelectedNodeId(null);
              }}
            >
              <Target className="h-3 w-3 mr-1" />
              Downstream
            </Button>
            <Button
              size="sm"
              variant={analysisMode === 'upstream' ? 'default' : 'outline'}
              className="h-7 text-xs"
              onClick={() => {
                setAnalysisMode(analysisMode === 'upstream' ? 'none' : 'upstream');
                if (analysisMode === 'upstream') setSelectedNodeId(null);
              }}
            >
              <GitBranch className="h-3 w-3 mr-1" />
              Upstream
            </Button>
            {analysisMode !== 'none' && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={resetAnalysis}>
                <RotateCcw className="h-3 w-3 mr-1" />
                Reset
              </Button>
            )}
          </div>
          {selectedNode && analysisMode !== 'none' && (
            <div className="text-xs text-muted-foreground pt-1 border-t">
              Selected: <span className="font-medium text-foreground">{selectedNode.data.label as string}</span>
              <span className="ml-1">
                ({highlightedIds.size} {analysisMode} node{highlightedIds.size !== 1 ? 's' : ''})
              </span>
            </div>
          )}
          {analysisMode !== 'none' && !selectedNode && (
            <div className="text-xs text-muted-foreground pt-1 border-t">
              Click a node to analyse
            </div>
          )}
        </div>
      </Panel>
    </ReactFlow>
  );
}
